import * as yup from "yup";

/** Strip HTML tags and trim for comment-required validation. */
const stripHtml = (html: string | null | undefined): string => {
	if (!html) {
		return "";
	}
	const doc = new DOMParser().parseFromString(html, "text/html");
	return (doc.body.textContent || "").trim();
};

const supplierSchema = yup.object({
	invite_id: yup.number().required(),
	answers: yup
		.array(
			yup.object({
				rfx_evaluation_compliance_question_id: yup.number().required(),
				is_compliant: yup.boolean().nullable().defined(),
			})
		)
		.required(),
	comments: yup.string().nullable().defined(),
	// null = not yet assessed. The "decision-required" test below turns an
	// undecided supplier into a validation error so the form's `isValid` (and
	// therefore the Proceed button) stays gated until every supplier is marked
	// Compliant or Non-Compliant for Evaluation.
	is_evaluated: yup.boolean().nullable().defined(),
})
	.test(
		"decision-required",
		"Mark this supplier Compliant or Non-Compliant for Evaluation",
		function (supplier) {
			if (supplier.is_evaluated === null || supplier.is_evaluated === undefined) {
				return this.createError({
					path: `${this.path}.is_evaluated`,
					message:
						"Mark this supplier Compliant or Non-Compliant for Evaluation",
				});
			}
			return true;
		}
	)
	.test(
		"all-toggled-when-evaluated",
		"All compliance questions must be answered before marking a supplier as Compliant for Evaluation",
		function (supplier) {
			if (!supplier.is_evaluated) {
				return true;
			}
			const allAnswered = (supplier.answers ?? []).every(
				(a) => a.is_compliant !== null && a.is_compliant !== undefined
			);
			if (!allAnswered) {
				return this.createError({
					path: `${this.path}.is_evaluated`,
					message:
						"All compliance questions must be answered before marking a supplier as Compliant for Evaluation",
				});
			}
			return true;
		}
	)
	.test(
		"comment-required",
		"please provide details of why non-compliant application is being evaluated",
		function (supplier) {
			if (!supplier.is_evaluated) {
				return true;
			}
			const hasNonCompliant = (supplier.answers ?? []).some(
				(a) => a.is_compliant === false
			);
			if (!hasNonCompliant) {
				return true;
			}
			const commentText = stripHtml(supplier.comments);
			if (!commentText) {
				return this.createError({
					path: `${this.path}.comments`,
					message:
						"please provide details of why non-compliant application is being evaluated",
				});
			}
			return true;
		}
	);

export const complianceGridSchema = yup.object({
	suppliers: yup.array(supplierSchema).required(),
});

export type ComplianceGridFormValues = {
	suppliers: Array<{
		invite_id: number;
		answers: Array<{
			rfx_evaluation_compliance_question_id: number;
			is_compliant: boolean | null;
		}>;
		comments: string | null;
		is_evaluated: boolean | null;
	}>;
};
